// FXCurrenciesPage.js - Real Economy & Geopolitical Forex
import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';

import FreeShopIndicator from '../components/dutyfree_indicator'
import TravelFXCard from '../components/travelcard'
import { NewsCardsSection } from '../components/NewsCards_Real';
import { useCurrencies } from '../components/useMarketData'; // Adjust path if necessary
// ============================================
// REAL DATA SIMULATION (API MOCKS)
// ============================================

const cities = ['cuzco', 'nyc', 'paris'];
// 1. FOREX DATA (Connect to: Alpha Vantage / ExchangeRate-API)
const FETCH_FX_DATA = async () => {
  // In production: await fetch('https://api.exchangerate-api.com/v4/latest/USD');
  return [
    { pair: 'GBP/USD', name: 'The Cable', rate: 1.26, change: -0.15, region: 'G7', type: 'Fi', lore: 'Oldest pair. Traded via trans-atlantic cable.' },
    { pair: 'EUR/USD', name: 'The Fiber', rate: 1.08, change: +0.22, region: 'G7', type: 'Fi', lore: 'Most traded pair globally.' },
    { pair: 'USD/BRL', name: 'Real', rate: 5.88, change: +1.20, region: 'BRICS', type: 'Fi', lore: 'Commodity heavy. High carry trade potential.' },
    { pair: 'NZD/USD', name: 'The Kiwi', rate: 0.61, change: +0.85, region: 'Oceania', type: 'Ag', lore: 'Bio-tech backed. Psa-resistant Gold3 Kiwi exports drive value.' },
    { pair: 'USD/JPY', name: 'The Gopher', rate: 151.2, change: -0.40, region: 'G7', type: 'Safe', lore: 'Carry trade funding currency.' },
  ];
};

// 2. TRAVEL INDEX (Connect to: Amadeus / Skyscanner / AirDNA)
// "The Real Economy Purchasing Power"
const FETCH_TRAVEL_INDEX = async () => {
  return [
    { destination: 'Nova York', flight: 2800, stay: 850, total: 3650, trend: 'up' },
    { destination: 'Lisboa', flight: 2900, stay: 550, total: 3450, trend: 'stable' },
    { destination: 'Buenos Aires', flight: 1200, stay: 380, total: 1580, trend: 'down' }, // "Blue Dollar" effect
    { destination: 'Tóquio', flight: 4500, stay: 600, total: 5100, trend: 'up' },
  ];
};

// 3. COMMERCE & MACRO (Connect to: TradingEconomics / Comex Stat)
const FETCH_COMMERCE_DATA = async () => {
  return [
    { month: 'Set', export: 28.5, import: 21.2, balance: 7.3 },
    { month: 'Out', export: 30.1, import: 22.8, balance: 7.3 },
    { month: 'Nov', export: 29.8, import: 23.5, balance: 6.3 },
    { month: 'Dez', export: 32.2, import: 24.1, balance: 8.1 }, // Record High
    { month: 'Jan', export: 31.5, import: 23.9, balance: 7.6 },
  ];
};

// ============================================
// MOCK DATA
// ============================================

const FX_RATES = [
  { pair: 'BRL/USD', rate: 5.42, change: +2.1, emoji: '🇧🇷🇺🇸' },
  { pair: 'BRL/EUR', rate: 5.88, change: +1.8, emoji: '🇧🇷🇪🇺' },
  { pair: 'BRL/GBP', rate: 6.85, change: +2.3, emoji: '🇧🇷🇬🇧' },
  { pair: 'BRL/CNY', rate: 0.75, change: +1.2, emoji: '🇧🇷🇨🇳' },
  { pair: 'BRL/ARS', rate: 0.0052, change: -0.5, emoji: '🇧🇷🇦🇷' },
];

// ============================================
// COMPONENTS
// ============================================

const CurrencyClusters = ({ theme, data }) => {
  // Group by Geopolitics/Region
  const clusters = {
    'G7 / NATO': data.filter(d => d.region === 'G7'),
    'BRICS / Commodities': data.filter(d => d.region === 'BRICS' || d.region === 'Oceania'),
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
      {Object.entries(clusters).map(([key, items]) => (
        <div key={key} style={{ 
          background: theme.cardBg, 
          borderRadius: '16px', 
          padding: '1.5rem',
          border: `1px solid ${theme.border}`
        }}>
          <h3 style={{ 
            fontSize: '0.9rem', 
            textTransform: 'uppercase', 
            letterSpacing: '1px', 
            color: theme.textSecondary,
            marginBottom: '1rem',
            borderBottom: `1px solid ${theme.border}`,
            paddingBottom: '0.5rem'
          }}>
            {key}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{item.pair}</div>
                  <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{item.name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{item.rate}</div>
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: item.change >= 0 ? '#4ade80' : '#ef4444' 
                  }}>
                    {item.change > 0 ? '+' : ''}{item.change}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const LoreCard = ({ theme, pair, lore, title }) => (
  <div style={{
    background: `linear-gradient(135deg, ${theme.cardBg}, rgba(0,0,0,0))`,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    padding: '1.5rem',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{ position: 'relative', zIndex: 2 }}>
      <div style={{ 
        display: 'inline-block', 
        padding: '4px 8px', 
        borderRadius: '4px', 
        background: 'rgba(59, 130, 246, 0.2)', 
        color: '#60a5fa', 
        fontSize: '0.7rem', 
        fontWeight: 'bold',
        marginBottom: '0.5rem'
      }}>
        {pair} • DEEP DIVE
      </div>
      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ fontSize: '0.9rem', color: theme.textSecondary, lineHeight: '1.5' }}>
        {lore}
      </p>
    </div>
    <div style={{ 
      position: 'absolute', 
      right: '-20px', 
      bottom: '-20px', 
      fontSize: '5rem', 
      opacity: 0.1, 
      pointerEvents: 'none' 
    }}>
      🥝
    </div>
  </div>
);

const TravelPowerIndex = ({ theme, data }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
      <div>
        <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
          Índice de Poder de Viagem
        </h3>
        <p style={{ fontSize: '0.85rem', color: theme.textSecondary }}>
          Custo Real: Voo (Ida/Volta) + 3 Dias Airbnb
        </p>
      </div>
      <div style={{ fontSize: '2rem' }}>✈️</div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {data.map((item, i) => (
        <div key={i} style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '1rem',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '8px',
          border: `1px solid ${theme.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
             <div style={{ 
               width: '4px', 
               height: '40px', 
               borderRadius: '2px', 
               background: item.trend === 'up' ? '#ef4444' : item.trend === 'down' ? '#4ade80' : '#fbbf24' 
             }} />
             <div>
               <div style={{ fontWeight: 'bold' }}>{item.destination}</div>
               <div style={{ fontSize: '0.8rem', color: theme.textSecondary }}>
                 Voo: R$ {item.flight} • Stay: R$ {item.stay}
               </div>
             </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', fontFamily: 'monospace' }}>
              R$ {item.total}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>
              Total Est.
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FXPairCard = ({ pair, rate, change, emoji, theme }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '12px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s',
    cursor: 'pointer'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.borderColor = change >= 0 ? '#ef4444' : '#4ade80';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.borderColor = theme.border;
  }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
      <div style={{ fontSize: '2rem' }}>{emoji}</div>
      <div style={{
        fontSize: '1rem',
        fontWeight: 'bold',
        color: change >= 0 ? '#ef4444' : '#4ade80',
        background: change >= 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(74, 222, 128, 0.1)',
        padding: '4px 12px',
        borderRadius: '6px'
      }}>
        {change >= 0 ? '+' : ''}{change}%
      </div>
    </div>
    
    <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '0.5rem' }}>
      {pair}
    </div>
    <div style={{
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: theme.textPrimary,
      fontFamily: 'monospace'
    }}>
      {rate.toFixed(2)}
    </div>
  </div>
);

const MacroChart = ({ theme, data }) => (
  <div style={{
    background: theme.cardBg,
    borderRadius: '16px',
    padding: '1.5rem',
    border: `1px solid ${theme.border}`,
    backdropFilter: 'blur(10px)',
    height: '100%'
  }}>
    <h3 style={{ fontSize: '1.3rem', fontWeight: 'bold', color: theme.textPrimary, marginBottom: '0.5rem' }}>
      Fluxo Comercial (Brasil)
    </h3>
    <p style={{ fontSize: '0.85rem', color: theme.textSecondary, marginBottom: '1.5rem' }}>
      Impacto direto no BRL (Exportação = Entrada de Dólar)
    </p>

    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
        <XAxis dataKey="month" stroke={theme.textSecondary} tick={{fontSize: 12}} />
        <YAxis stroke={theme.textSecondary} tick={{fontSize: 12}} />
        <Tooltip
          contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '8px' }}
          formatter={(value) => [`US$ ${value} Bi`, '']}
        />
        <Bar dataKey="export" stackId="a" fill="#4ade80" radius={[4, 4, 0, 0]} />
        <Bar dataKey="import" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
    
    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem', fontSize: '0.8rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ width: '10px', height: '10px', background: '#4ade80', borderRadius: '2px' }} />
        <span>Export (Entrada)</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ width: '10px', height: '10px', background: '#ef4444', borderRadius: '2px' }} />
        <span>Import (Saída)</span>
      </div>
    </div>
  </div>
);

// ============================================
// MAIN PAGE
// ============================================

export default function FXCurrenciesPage() {
  const [isDark, setIsDark] = useState(true);
  const [fxData, setFxData] = useState([]);
  const [travelData, setTravelData] = useState([]);
  const [commerceData, setCommerceData] = useState([]);
  const [loading, setLoading] = useState(true);

// 1. Fetch the currency data from your Go API
  const { data: apiCurrencies, loading: currenciesLoading } = useCurrencies();

// 2. Format the data for the Header Ticker
  const getFxTickers = useMemo(() => {
    if (!apiCurrencies || !Array.isArray(apiCurrencies)) return [];

    return apiCurrencies
      // Safety Check: Ensure we have a valid number and don't divide by zero
      .filter(item => typeof item.last_quote === 'number' && item.last_quote !== 0) 
      .map(item => ({
        ticker: item.symbol,       
        name: item.name,           
        
        // TRICK: Invert the price to get "Value in USD"
        // Example: 1 USD = 5.00 BRL becomes 1 BRL = $0.20 USD
        ult: 1 / item.last_quote,      

        // TRICK: Invert the variation sign
        // If USD/BRL goes UP (+), the BRL value actually goes DOWN (-)
        var: item.rate * -1             
      }));
  }, [apiCurrencies]);
  // Theme Config
  const theme = {
    background: isDark ? 'rgba(5, 5, 5, 0.95)' : 'rgba(245, 245, 245, 0.95)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#666',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
  };

  // Simulate Real Data Fetching
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const fx = await FETCH_FX_DATA();
      const tr = await FETCH_TRAVEL_INDEX();
      const co = await FETCH_COMMERCE_DATA();
      
      setFxData(fx);
      setTravelData(tr);
      setCommerceData(co);
      setLoading(false);
    };
    loadData();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#000' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
      
      {/* Background Effect */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <Silk speed={12} scale={3} color={isDark ? "#1e293b" : "#cbd5e1"} noiseIntensity={isDark ? 5 : 2} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)} topTickers={getFxTickers} currency="USD"/>

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
          
          {/* Hero */}
          <div style={{ marginBottom: '4rem', marginTop: '2rem' }}>
            <h1 style={{ 
              fontSize: '3.5rem', 
              fontWeight: '900', 
              letterSpacing: '-2px', 
              marginBottom: '0.5rem',
              background: 'linear-gradient(to right, #60a5fa, #3b82f6)', 
              backgroundClip: 'text', 
              WebkitBackgroundClip: 'text', 
              color: 'transparent'
            }}>
              Forex & Economia Real
            </h1>
            <p style={{ fontSize: '1.2rem', color: theme.textSecondary, maxWidth: '600px' }}>
              Dados geopolíticos, fluxo comercial e poder de compra real para viajantes.
            </p>
          </div>

 {/* Section 1: Major FX Pairs */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', color: theme.textPrimary }}>
              💱 Principais Pares
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
              {FX_RATES.map((fx, i) => (
                <FXPairCard key={i} {...fx} theme={theme} />
              ))}
            </div>
          </div>


<div style={{ 
      background: '#09090b', minHeight: '100vh', padding: '40px',
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '24px', justifyContent: 'center'
    }}>
      {cities.map(id => (
        <TravelFXCard key={id} cityId={id} />
      ))}
    </div>
<FreeShopIndicator theme={theme}/>


          {/* 1. Geopolitical Clusters */}
          <section style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>Geopolítica Cambial</h2>
              <span style={{ 
                fontSize: '0.75rem', 
                background: 'rgba(34, 197, 94, 0.1)', 
                color: '#22c55e', 
                border: '1px solid rgba(34, 197, 94, 0.3)', 
                padding: '4px 12px', 
                borderRadius: '100px', 
                fontWeight: 'bold' 
              }}>
                G7 • BRICS • Commodities
              </span>
            </div>
            {loading ? <p>Carregando dados...</p> : <CurrencyClusters theme={theme} data={fxData} />}
          </section>


              <NewsCardsSection theme={theme} itemsPerRow={3} />

          {/* 2. Lore & Deep Dive (The Kiwi / QE Story) */}
          <section style={{ marginBottom: '4rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
             <LoreCard 
               theme={theme} 
               pair="NZD/USD" 
               title="O 'Dólar Kiwi' e a Biotecnologia"
               lore="O 'QE' (Kiwi) não é apenas uma fruta, é um triunfo da biotecnologia. Após a bactéria Psa devastar as plantações em 2010, a cultivar 'Gold3' (resistente) foi desenvolvida, salvando o setor e blindando o PIB da Nova Zelândia. Hoje, o valor da moeda flutua com a safra."
             />
             <LoreCard 
               theme={theme} 
               pair="Carry Trade" 
               title="Diferencial de Juros (Selic vs Fed)"
               lore="O 'Carry Trade' clássico: tomar dinheiro emprestado no Japão (juro zero) e aplicar no Brasil (Selic alta). O lucro não vem só do juro, mas da valorização do Real. Quando a volatilidade sobe, esse trade se desfaz rapidamente."
             />
          </section>

          {/* 3. Real Economy Indicators */}
          <section>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Economia Real & Viagem</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '2rem' }}>
              {/* Travel Index */}
              <TravelPowerIndex theme={theme} data={travelData} />
              
              {/* Macro Data */}
              <MacroChart theme={theme} data={commerceData} />
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}